import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
const MONTSERRAT = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`;

// ── DESIGN SYSTEM ────────────────────────────────────────────────────────────
const DS = {
  indigo:       "#4F46E5",
  indigoMid:    "#6366F1",
  indigoLight:  "#EEF2FF",
  indigoDark:   "#3730A3",
  teal:         "#0D9488",
  tealLight:    "#F0FDFA",
  emerald:      "#059669",
  emeraldLight: "#ECFDF5",
  amber:        "#D97706",
  amberLight:   "#FFFBEB",
  rose:         "#E11D48",
  roseLight:    "#FFF1F2",
  violet:       "#7C3AED",
  violetLight:  "#F5F3FF",
  orange:       "#EA580C",
  orangeLight:  "#FFF7ED",
  bg:           "#F8FAFC",
  surface:      "#FFFFFF",
  surfaceAlt:   "#F8FAFC",
  border:       "#E2E8F0",
  dark:         "#0F172A",
  gray:         "#64748B",
  grayLight:    "#F1F5F9",
  faint:        "#94A3B8",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [hov, setHov] = useState(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const res = await API.get("/admin/audit-logs", { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) setLogs(res.data.data || []);
      } catch (err) {
        console.error("Error reading system security audit logs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => 
    (log.user || "").toLowerCase().includes(search.toLowerCase()) ||
    (log.action || "").toLowerCase().includes(search.toLowerCase()) ||
    (log.module || "").toLowerCase().includes(search.toLowerCase()) ||
    (log.ip || "").toLowerCase().includes(search.toLowerCase())
  );

  const successCount = logs.filter(log => log.status === "Success").length;
  const failedCount = logs.filter(log => log.status === "Failed").length;
  const activeUsersCount = new Set(logs.map(l => l.user)).size;

  return (
    <AdminLayout>
      <style>{MONTSERRAT + ` * { font-family: 'Montserrat', sans-serif !important; }`}</style>
      <div style={{ flex: 1, padding: "28px 28px", overflowY: "auto" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🔎</div>
          <div>
            <h1 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 26, color: DS.dark, margin: 0 }}>Audit Logs</h1>
            <div style={{ fontSize: 13, color: DS.gray, marginTop: 4 }}>Trace security credentials, system write events, and user activities</div>
          </div>
        </div>

        {/* STATS CARDS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 24 }}>
          {/* Total Logs */}
          <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, padding: "20px 24px", position: "relative", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: DS.indigoMid }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Activities</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: DS.indigoMid, marginTop: 8 }}>{loading ? "..." : logs.length}</div>
          </div>
          {/* Successful */}
          <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, padding: "20px 24px", position: "relative", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: DS.emerald }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.05em" }}>Successful Actions</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: DS.emerald, marginTop: 8 }}>{loading ? "..." : successCount}</div>
          </div>
          {/* Failed */}
          <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, padding: "20px 24px", position: "relative", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: DS.rose }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.05em" }}>Failed Operations</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: DS.rose, marginTop: 8 }}>{loading ? "..." : failedCount}</div>
          </div>
          {/* Active Users */}
          <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, padding: "20px 24px", position: "relative", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: DS.indigo }} />
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.05em" }}>Active Users</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: DS.indigo, marginTop: 8 }}>{loading ? "..." : activeUsersCount}</div>
          </div>
        </div>

        {/* LOGS TABLE */}
        <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#E11D48,#D97706)" }} />
          
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${DS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 16, color: DS.dark }}>Activity Logs</div>
              <div style={{ fontSize: 12, color: DS.gray, marginTop: 2 }}>{filteredLogs.length} of {logs.length} operations tracked</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 9, background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`, borderRadius: 12, padding: "9px 14px", minWidth: 280 }}>
              <span style={{ fontSize: 14, color: DS.faint }}>🔍</span>
              <input type="text" placeholder="Search user, action, module, IP..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ border: "none", background: "transparent", fontSize: 13, color: DS.dark, outline: "none", width: "100%", fontFamily: "inherit" }} />
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
              <thead>
                <tr style={{ background: DS.surfaceAlt, borderBottom: `1px solid ${DS.border}` }}>
                  {["#", "User Profile", "Operation Action", "Target Module", "IP Address", "Timestamp", "Status"].map((h, i) => (
                    <th key={h} style={{ fontSize: 11, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.07em", padding: "12px 18px", textAlign: i === 0 ? "center" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ padding: "40px", textAlign: "center", color: DS.faint, fontSize: 14 }}>Loading system activity logs...</td>
                  </tr>
                ) : filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: "40px", textAlign: "center", color: DS.faint, fontSize: 14 }}>No audit logs found.</td>
                  </tr>
                ) : (
                  filteredLogs.map((log, i) => {
                    const isSuccess = log.status === "Success";
                    return (
                      <tr key={log.id} onMouseEnter={() => setHov(log.id)} onMouseLeave={() => setHov(null)}
                        style={{ borderBottom: `1px solid ${DS.border}`, background: hov === log.id ? DS.surfaceAlt : DS.surface, transition: "background 0.15s" }}>
                        
                        {/* Number */}
                        <td style={{ padding: "15px 18px", textAlign: "center" }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: DS.faint }}>{i + 1}</span>
                        </td>

                        {/* User */}
                        <td style={{ padding: "15px 18px", fontSize: 14, fontWeight: 700, color: DS.dark }}>
                          {log.user}
                        </td>

                        {/* Action */}
                        <td style={{ padding: "15px 18px", fontSize: 13, fontWeight: 600, color: DS.dark }}>
                          {log.action}
                        </td>

                        {/* Module */}
                        <td style={{ padding: "15px 18px" }}>
                          <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, padding: "4px 8px", borderRadius: 6 }}>
                            {log.module}
                          </span>
                        </td>

                        {/* IP Address */}
                        <td style={{ padding: "15px 18px" }}>
                          <span style={{ fontFamily: "monospace", fontSize: 12, color: DS.gray, background: DS.grayLight, padding: "3px 6px", borderRadius: 6 }}>
                            {log.ip || "127.0.0.1"}
                          </span>
                        </td>

                        {/* Timestamp */}
                        <td style={{ padding: "15px 18px", fontSize: 13, color: DS.gray }}>
                          {log.date}
                        </td>

                        {/* Status */}
                        <td style={{ padding: "15px 18px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: isSuccess ? DS.emerald : DS.rose, background: isSuccess ? DS.emeraldLight : DS.roseLight, padding: "5px 12px", borderRadius: 20, border: `1px solid ${isSuccess ? "#A7F3D0" : "#FECDD3"}` }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ padding: "14px 22px", borderTop: `1px solid ${DS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: DS.surfaceAlt, flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 12, color: DS.faint }}>Showing {filteredLogs.length} of {logs.length} entries</span>
            <div style={{ display: "flex", gap: 5 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: DS.indigo, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>1</div>
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}